import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { api, track } from "./api";

const StoreContext = createContext(null);
const read = (k, f) => { const raw = localStorage.getItem(k); try { return JSON.parse(raw) ?? f; } catch { return raw ?? f; } };

export function StoreProvider({ children }) {
  const [cart, setCart] = useState(() => read("hh_cart", []));
  const [wishlist, setWishlist] = useState(() => read("hh_wishlist", []));
  const [recent, setRecent] = useState(() => read("hh_recent", []));
  const [coupon, setCoupon] = useState(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [settings, setSettings] = useState(null);
  const [consent, setConsent] = useState(() => read("hh_consent", null));
  const consentShown = useRef(false);

  useEffect(() => { localStorage.setItem("hh_cart", JSON.stringify(cart)); }, [cart]);
  useEffect(() => { localStorage.setItem("hh_wishlist", JSON.stringify(wishlist)); }, [wishlist]);
  useEffect(() => { localStorage.setItem("hh_recent", JSON.stringify(recent)); }, [recent]);

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const trackEvent = useCallback(track, []);

  const addToCart = useCallback((product, variant, qty = 1, { silent } = {}) => {
    setCart((c) => {
      const i = c.findIndex((l) => l.variant_id === variant.id);
      const line = {
        product_id: product.slug, variant_id: variant.id, slug: product.slug,
        name: product.name, variant_name: variant.name, price: variant.price,
        mrp: variant.mrp, image: variant.image || (product.images?.[0]?.url),
        image_alt: product.images?.[0]?.alt || product.name, qty: Math.min((c[i]?.qty || 0) + qty, 10),
      };
      if (i >= 0) { const next = [...c]; next[i] = { ...next[i], qty: Math.min(next[i].qty + qty, 10) }; return next; }
      return [...c, line];
    });
    trackEvent("add_to_cart", { items: [{ item_id: product.slug, variant_id: variant.id, quantity: qty, price: variant.price, currency: "INR" }] });
    if (!silent) setCartOpen(true); // Ceramide behaviour: slide-out bag opens on add
  }, [trackEvent]);

  const updateQty = useCallback((variantId, qty) => {
    setCart((c) => (qty <= 0 ? c.filter((l) => l.variant_id !== variantId)
      : c.map((l) => (l.variant_id === variantId ? { ...l, qty: Math.min(qty, 10) } : l))));
  }, []);

  const removeFromCart = useCallback((variantId) => {
    setCart((c) => c.filter((l) => l.variant_id !== variantId));
  }, []);

  const clearCart = useCallback(() => setCart([]), []);
  const restoreCart = useCallback((lines) => setCart(lines.map((l) => ({
    product_id: l.slug, variant_id: l.variant_id, slug: l.slug, name: l.name, variant_name: l.variant_name,
    price: l.price, image: l.image, image_alt: l.name, qty: Math.min(l.qty || 1, 10) }))), []);

  const toggleWishlist = useCallback((product) => {
    setWishlist((w) => {
      const exists = w.some((p) => p.slug === product.slug);
      return exists ? w.filter((p) => p.slug !== product.slug) : [...w, product];
    });
  }, []);

  const inWishlist = useCallback((slug) => wishlist.some((p) => p.slug === slug), [wishlist]);

  const pushRecent = useCallback((product) => {
    setRecent((r) => [{ slug: product.slug, name: product.name, price: product.price, mrp: product.mrp,
      image: product.images?.[0]?.url, category: product.category },
      ...r.filter((x) => x.slug !== product.slug)].slice(0, 8));
  }, []);

  const subtotal = cart.reduce((s, l) => s + l.price * l.qty, 0);
  const cartCount = cart.reduce((s, l) => s + l.qty, 0);

  const value = {
    cart, addToCart, updateQty, removeFromCart, clearCart, restoreCart, subtotal, cartCount,
    wishlist, toggleWishlist, inWishlist, recent, pushRecent,
    coupon, setCoupon, cartOpen, setCartOpen, searchOpen, setSearchOpen,
    settings, consent, setConsent, track: trackEvent,
  };
  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
}

export const useStore = () => useContext(StoreContext);
